import { Box, Typography, Stack, Divider, Card, Button, Chip } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IconButton, Tooltip } from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LanguageIcon from '@mui/icons-material/Language';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getCourseDetails } from '../../../redux/features/courseSlice';
import { useSelector } from 'react-redux';
import { uploadedFileUrl, uploadedFileUrlContabo } from '../../../utils/constants/links';
import Loader3D from '../../../components/Loader/Loader3D';
import { getEmbedUrl } from './utils/Functions';

export default function CoursePreview() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const courseDetails = useSelector((state) => state.course.courseDetails?.data?.[0] ?? {});
  const courseDetailsLoading = useSelector((state) => state.course.courseDetails?.loading);

  useEffect(() => {
    dispatch(getCourseDetails({ course_available_detail_id: id }));
  }, [id, dispatch]);

  return (
    <GlobalWrapper title={'Course Preview'}>
      {courseDetailsLoading ? (
        <Loader3D />
      ) : (
        <Box
          position={'relative'}
          p={3}
          sx={{
            minHeight: '100vh',
            bgcolor: 'linear-gradient(135deg, #f3f4ff 0%, #fdfbff 40%, #e3f2fd 100%)',
          }}
        >
          <Stack>
            <Button
              component="a"
              href={`${uploadedFileUrlContabo}/${courseDetails?.brochure_url}`}
              target="_blank"
              sx={{
                width: 120,
                alignSelf: 'flex-end',
              }}
              download
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<DownloadForOfflineIcon />}
            >
              brochure
            </Button>
            <Stack spacing={3} mt={1} position="relative" zIndex={1}>
              <Box display={'flex'} gap={2} alignItems={'center'}>
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    pl={2}
                    sx={{
                      letterSpacing: 0.2,
                      color: 'text.primary',
                    }}
                  >
                    {courseDetails?.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    pl={2}
                    sx={{
                      letterSpacing: 0.1,
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    {`${courseDetails?.project_name} | ${courseDetails?.course_type}`}
                  </Typography>
                </Box>
                <Chip
                  label={courseDetails?.course_availability ? 'Active' : 'In active'}
                  color={courseDetails?.course_availability ? 'success' : 'error'}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    alignSelf: 'flex-start',
                  }}
                />
              </Box>
              <Typography
                p={2}
                sx={{
                  // color: 'text.secondary',
                  fontSize: { xs: 14.5, md: 15.5 },
                  lineHeight: 1.7,
                }}
              >
                {courseDetails?.description}
              </Typography>

              <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

              {/* Highlights */}
              <Box p={2}>
                <Typography
                  fontWeight={600}
                  sx={{
                    fontSize: 16,
                    mb: 1,
                    color: 'primary.main',
                  }}
                >
                  Course Highlights
                </Typography>
                {courseDetails?.highlights?.map((h, i) => (
                  <Box
                    key={i}
                    component="li"
                    sx={{
                      color: 'text.secondary',
                      fontSize: 14,
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>

              {/* Course Details */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <Typography
                  fontWeight={600}
                  sx={{
                    fontSize: 16,
                    mb: 1,
                    color: 'primary.main',
                  }}
                >
                  Course Details
                </Typography>
                <Stack direction="column" spacing={2} sx={{ flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>
                      Duration:
                    </Typography>
                    {courseDetails?.duration}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>
                      Focus Area:
                    </Typography>
                    {courseDetails?.focus_area}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>
                      Min Sessions / Week:
                    </Typography>
                    {courseDetails?.min_session_per_week}
                  </Typography>
                </Stack>
              </Box>

              {/* Reasons */}
              <Box p={2}>
                <Typography
                  fontWeight={600}
                  sx={{
                    fontSize: 16,
                    mb: 1,
                    color: 'primary.main',
                  }}
                >
                  Why Choose This Course
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    pl: 3,
                    m: 0,
                    display: 'column',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1,
                    listStyle: 'disc',
                  }}
                >
                  {courseDetails?.reasons?.map((r, i) => (
                    <Box
                      key={i}
                      component="li"
                      sx={{
                        color: 'text.secondary',
                        fontSize: 14,
                      }}
                    >
                      {r}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Stack>

            {/* Banner */}
            {courseDetails?.course_banner_url && (
              <Box
                component="img"
                src={`${uploadedFileUrlContabo}/${courseDetails?.course_banner_url}`}
                sx={{
                  width: '100%',
                  height: { xs: 200, md: 240 },
                  objectFit: 'cover',
                  borderRadius: 3,
                  mt: 3,
                  //   boxShadow: '0 10px 30px rgba(15,23,42,0.28)',
                }}
              />
            )}
            <Box>
              <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ color: 'text.secondary' }} p={2}>
                <Stack direction="row" spacing={0.7} alignItems="center">
                  <ScheduleIcon fontSize="small" />
                  <Typography variant="body2">{courseDetails?.duration} Sessions</Typography>
                </Stack>

                <Stack direction="row" spacing={0.7} alignItems="center">
                  <MenuBookIcon fontSize="small" />
                  <Typography variant="body2">{courseDetails?.lessons} Lessons</Typography>
                </Stack>

                <Stack direction="row" spacing={0.7} alignItems="center">
                  <LanguageIcon fontSize="small" />
                  <Typography variant="body2">Language : {courseDetails?.languages?.join(' , ')}</Typography>
                </Stack>

                <Stack direction="row" spacing={0.7} alignItems="center">
                  <WorkspacePremiumIcon fontSize="small" />
                  <Typography variant="body2">Certificate : {courseDetails?.certificate ? 'Yes' : 'No'}</Typography>
                </Stack>
              </Stack>
            </Box>

            {/* Testimonial Video */}
            {courseDetails?.testimonial_video_link && (
              <Box mt={4}>
                <Typography
                  fontWeight={600}
                  sx={{
                    pl: 2,
                    fontSize: 16,
                    mb: 1,
                    color: 'primary.main',
                  }}
                >
                  Testimonial Video
                </Typography>

                <Box
                  component={'iframe'}
                  src={getEmbedUrl(courseDetails?.testimonial_video_link)}
                  title="Testimonial Video"
                  width="100%"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  sx={{ border: 'none', height: { xs: 250, md: 340 }, borderRadius: 3 }}
                />
                {/* URL + Copy */}
                <Box
                  mt={1}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '90%',
                    }}
                  >
                    {courseDetails?.testimonial_video_link}
                  </Typography>

                  <Tooltip title="Copy video URL">
                    <IconButton
                      size="small"
                      onClick={() => navigator.clipboard.writeText(courseDetails?.testimonial_video_link)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </GlobalWrapper>
  );
}
